import Config from '@/config'
import { ApiRequestError } from '@/errors/ApiRequestError'
import type { ApisauceInstance } from 'apisauce'
import { create } from 'apisauce'
import { authRequestInterceptor } from './interceptors/auth'
import type {
	ChatListMessagesResponse,
	ChatMessage,
	ChatUploadResponse,
	LocalAttachment,
} from './types/MessagingApi'

const MESSAGE_LIMIT = 100

/** The patient's support chat on messaging-kapi. Every call throws on failure with the status. */
export class MessagingApi {
	_apiConfig: ApisauceInstance

	constructor() {
		this._apiConfig = create({
			baseURL: Config.MESSAGING_API_URL_BASE,
			timeout: 10000,
		})

		this._apiConfig.axiosInstance.interceptors.request.use(authRequestInterceptor)
	}

	listChatMessages() {
		return this._apiConfig
			.get('/member-chat/messages', { limit: MESSAGE_LIMIT })
			.then((response) => {
				if (!response.ok) {
					throw new ApiRequestError(
						`failed_to_list_chat_messages: ${response.problem}`,
						response.status
					)
				}
				return response.data as ChatListMessagesResponse
			})
	}

	/** Uploads one picked file; the asset id it returns goes on the message. */
	uploadChatAttachment(file: LocalAttachment) {
		const form = new FormData()
		// React Native's FormData takes a file descriptor object, not a Blob.
		form.append('file', {
			uri: file.uri,
			name: file.filename,
			type: file.contentType,
		} as unknown as Blob)
		return this._apiConfig
			.post('/member-chat/attachments', form, {
				headers: { 'Content-Type': 'multipart/form-data' },
				timeout: 60000,
			})
			.then((response) => {
				if (!response.ok) {
					const message =
						(response.data as { message?: string } | undefined)?.message ??
						`failed_to_upload_chat_attachment: ${response.problem}`
					throw new ApiRequestError(message, response.status)
				}
				return response.data as ChatUploadResponse
			})
	}

	sendChatMessage(body: string, assetIds: number[] = []) {
		return this._apiConfig
			.post('/member-chat/messages', { body, assetIds })
			.then((response) => {
				if (!response.ok) {
					const message =
						(response.data as { message?: string } | undefined)?.message ??
						`failed_to_send_chat_message: ${response.problem}`
					throw new ApiRequestError(message, response.status)
				}
				return response.data as ChatMessage
			})
	}

	markChatMessagesConsumed(messageIds: number[]) {
		return this._apiConfig
			.post('/member-chat/messages/consumed', { messageIds })
			.then((response) => {
				if (!response.ok) {
					throw new ApiRequestError(
						`failed_to_mark_chat_messages_consumed: ${response.problem}`,
						response.status
					)
				}
			})
	}
}

export const messagingApi = new MessagingApi()
